// MoQ transport behind the LiveKit-shaped API (moq.dev, relay moq-relay).
//
// Layout on the relay, relative to the room root in the connection URL:
//   <identity>/camera.hang   microphone ("audio") + camera ("video/hd", "video/sd")
//   <identity>/screen.hang   screen share video + screen share audio
// plus the @moq/room metadata tracks (hang/user.json, hang/preview.json) and
// parley's data track (parley/data.json) on the camera broadcast.
// <identity> is the Fluxer voice identity `user_<userId>_<connectionId>`.
//
// The camera broadcast is announced for as long as we are connected, so a
// silent participant still appears. Mute state travels in preview.json.

import * as Json from '@moq/json';
import * as Moq from '@moq/net';
import * as Publish from '@moq/publish';
import {broadcastPath, consume, KIND, type Kind, parse, serve, userFields} from '@moq/room';
import {Effect, Signal} from '@moq/signals';
import * as Watch from '@moq/watch';
import {frameAAD, type MediaCrypto} from './e2ee';
import {OpenedBroadcast, SealedBroadcast} from './e2eeMoq';
import {type VideoEncoding, VideoQuality} from './enums';
import type {DataPublishOptions} from './options';
import type {LocalPublisher} from './participant';
import {installPlayoutWorklet, isSharedPlaybackContext} from './playoutWorklet';
import type {LocalTrackPublication, RemotePublicationControl} from './publication';
import {Track} from './track';

// Remote audio plays through our playout stage, not @moq/watch's ring
// (playoutWorklet.ts): concealment instead of cuts, an adaptive buffer.
installPlayoutWorklet();

const DATA_TRACK = 'parley/data.json';
/** Playback jitter buffer for remote media; see RemoteBroadcast. */
const PLAYBACK_JITTER = Moq.Time.Milli(100);
const DATA_WINDOW_MS = 10_000;
const SPEAKING_THRESHOLD = 0.012;
const SPEAKING_HOLD_MS = 400;

/**
 * The ceiling for a shared surface. WebCodecs hands us a software encoder on
 * most machines, and a 4K share through one measured 1.9 pictures a second at
 * the viewer, so "ultra" and "source" mean "as much as we can send"
 * (docs/strands/screenshare.md).
 */
export const SCREEN_MAX_PIXELS = 1920 * 1080;
/** `video/hd` carries the surface itself; `video/sd` is the rung for a thin
 * link — a quarter of the pixels, following the source instead of pinning a
 * size — with a quarter of the bitrate budget and a floor under it. */
const SCREEN_HD_DEFAULT: Publish.Video.Config = {maxPixels: SCREEN_MAX_PIXELS};
const SCREEN_SD_DEFAULT: Publish.Video.Config = {maxScale: 0.25, maxBitrate: 800_000};
const SCREEN_SD_BITRATE_SHARE = 0.25;
const SCREEN_SD_MIN_BITRATE = 300_000;

export type SessionStatus = 'connecting' | 'connected' | 'disconnected';

export interface DataMessage {
	seq: number;
	at: number;
	topic?: string;
	to?: Array<string>;
	payload: string; // base64
	/** payload is end-to-end encrypted (e2ee.ts) */
	e2ee?: boolean;
}

type DataSnapshot = {messages: Array<DataMessage>};

function toBase64(data: Uint8Array): string {
	let s = '';
	for (const b of data) s += String.fromCharCode(b);
	return btoa(s);
}

function fromBase64(s: string): Uint8Array {
	const bin = atob(s);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

/** Measures a MediaStreamTrack's level and reports speaking changes. */
export class SpeakingMeter {
	#ctx: AudioContext;
	#source?: MediaStreamAudioSourceNode;
	#analyser: AnalyserNode;
	#buffer: Float32Array<ArrayBuffer>;
	#timer: ReturnType<typeof setInterval>;
	#lastLoud = 0;
	#speaking = false;

	constructor(ctx: AudioContext, onChange: (speaking: boolean, level: number) => void, node?: AudioNode) {
		this.#ctx = ctx;
		this.#analyser = ctx.createAnalyser();
		this.#analyser.fftSize = 512;
		this.#buffer = new Float32Array(this.#analyser.fftSize);
		node?.connect(this.#analyser);
		this.#timer = setInterval(() => {
			this.#analyser.getFloatTimeDomainData(this.#buffer);
			let sum = 0;
			for (const v of this.#buffer) sum += v * v;
			const level = Math.sqrt(sum / this.#buffer.length);
			const now = performance.now();
			if (level > SPEAKING_THRESHOLD) this.#lastLoud = now;
			const speaking = now - this.#lastLoud < SPEAKING_HOLD_MS;
			if (speaking !== this.#speaking) {
				this.#speaking = speaking;
				onChange(speaking, Math.min(1, level * 10));
			}
		}, 100);
	}

	setTrack(track: MediaStreamTrack | undefined): void {
		this.#source?.disconnect();
		this.#source = undefined;
		if (track) {
			this.#source = this.#ctx.createMediaStreamSource(new MediaStream([track]));
			this.#source.connect(this.#analyser);
		}
	}

	close(): void {
		clearInterval(this.#timer);
		this.#source?.disconnect();
		this.#analyser.disconnect();
	}
}

/** Publishes the local participant's tracks. */
export class LocalMedia implements LocalPublisher {
	readonly #effect = new Effect();
	readonly #mic = new Signal<MediaStreamTrack | undefined>(undefined);
	readonly #micMuted = new Signal(false);
	readonly #camera = new Signal<MediaStreamTrack | undefined>(undefined);
	readonly #cameraMuted = new Signal(false);
	readonly #screen = new Signal<MediaStreamTrack | undefined>(undefined);
	readonly #screenAudio = new Signal<MediaStreamTrack | undefined>(undefined);
	readonly #micLive = new Signal(false);
	readonly #cameraLive = new Signal(false);
	readonly #screenLive = new Signal(false);
	readonly #preview = new Signal<Record<string, unknown>>({});
	/**
	 * Whether the relay token grants `put`. A token without it (server mute,
	 * deafen, timeout, no SPEAK/STREAM) means nothing is announced at all:
	 * the relay would not take it anyway.
	 */
	readonly #allowed = new Signal(true);
	/**
	 * The screen renditions' encoder knobs, live-editable. The stream settings
	 * (resolution, frame rate, bitrate; features/voice/utils/ScreenShareOptions)
	 * arrive as `screenShareEncoding` on the publication and land here: LiveKit
	 * applied them to the RTP sender, MoQ has no sender, so they belong to the
	 * encoder. Reset on unpublish so the next share starts from the defaults.
	 */
	readonly #screenHd = new Signal<Publish.Video.Config | undefined>(SCREEN_HD_DEFAULT);
	readonly #screenSd = new Signal<Publish.Video.Config | undefined>(SCREEN_SD_DEFAULT);
	readonly #data = new Signal<DataSnapshot>({messages: []});
	#seq = 0;
	readonly #crypto: MediaCrypto;
	readonly #dataAAD: Uint8Array;
	readonly camera: Publish.Broadcast;
	readonly screen: Publish.Broadcast;
	readonly speaking: SpeakingMeter;

	constructor(
		connection: Moq.Connection.Reload,
		identity: string,
		onSpeaking: (speaking: boolean, level: number) => void,
		crypto: MediaCrypto,
	) {
		this.#crypto = crypto;
		this.#dataAAD = frameAAD(identity, 'data', DATA_TRACK);
		const established = connection.established;
		const cameraCapture = new Publish.Video.Capture({source: this.#camera as Signal<Publish.Video.Source | undefined>});
		const screenCapture = new Publish.Video.Capture({source: this.#screen as Signal<Publish.Video.Source | undefined>});
		this.#effect.cleanup(() => cameraCapture.close());
		this.#effect.cleanup(() => screenCapture.close());

		// Audio and video frames are end-to-end encrypted while E2EE is on.
		this.camera = new SealedBroadcast(
			{
				connection: established,
				enabled: this.#allowed,
				name: broadcastPath(Moq.Path.from(identity), KIND.camera),
				display: cameraCapture.out.display,
			},
			crypto,
			identity,
			KIND.camera,
		);
		// The display size travels in the catalog and sizes the watcher's canvas.
		// Announce what we encode, not what we captured: a viewer of a 4K
		// surface that we send at 1080p would otherwise paint every frame onto
		// a 4K canvas, which cost more than the decoding did.
		const screenDisplay = new Signal<{width: number; height: number} | undefined>(undefined);
		this.screen = new SealedBroadcast(
			{
				connection: established,
				enabled: this.#screenLive,
				name: broadcastPath(Moq.Path.from(identity), KIND.screen),
				display: screenDisplay,
			},
			crypto,
			identity,
			KIND.screen,
		);
		this.#effect.cleanup(() => this.camera.close());
		this.#effect.cleanup(() => this.screen.close());

		const screenHd = new Publish.Video.Encoder('video/hd', {
			broadcast: this.screen,
			capture: screenCapture,
			enabled: this.#screenLive,
			config: this.#screenHd,
		});
		const videoEncoders: Array<[string, Publish.Video.Encoder]> = [
			[
				'camera hd',
				new Publish.Video.Encoder('video/hd', {
					broadcast: this.camera,
					capture: cameraCapture,
					enabled: this.#cameraLive,
					config: {maxPixels: 1280 * 720},
				}),
			],
			[
				'camera sd',
				new Publish.Video.Encoder('video/sd', {
					broadcast: this.camera,
					capture: cameraCapture,
					enabled: this.#cameraLive,
					config: {maxPixels: 640 * 360},
				}),
			],
			['screen hd', screenHd],
			[
				'screen sd',
				new Publish.Video.Encoder('video/sd', {
					broadcast: this.screen,
					capture: screenCapture,
					enabled: this.#screenLive,
					config: this.#screenSd,
				}),
			],
		];
		const encoders = [
			new Publish.Audio.Encoder('audio', {
				broadcast: this.camera,
				source: this.#mic as Signal<Publish.Audio.Source | undefined>,
				enabled: this.#micLive,
			}),
			...videoEncoders.map(([, enc]) => enc),
			new Publish.Audio.Encoder('audio', {
				broadcast: this.screen,
				source: this.#screenAudio as Signal<Publish.Audio.Source | undefined>,
				enabled: this.#screenLive,
			}),
		];
		this.#effect.run((effect) => {
			const resolved = effect.get(screenHd.out.resolved);
			const captured = effect.get(screenCapture.out.display);
			screenDisplay.set(resolved ? {width: resolved.width, height: resolved.height} : captured);
		});

		// What we really send, once the encoder has probed the machine: the
		// line to ask a user for when a share looks bad on the other side.
		for (const [label, enc] of videoEncoders) {
			this.#effect.run((effect) => {
				const config = effect.get(enc.out.resolved);
				if (!config) return;
				console.info(
					`[parley/rtc] encoding ${label} ${config.width}x${config.height}@${config.framerate ?? '?'} ` +
						`${config.codec} ${config.bitrate ? Math.round(config.bitrate / 1000) : '?'} kbit/s ` +
						`(${config.hardwareAcceleration ?? 'no-preference'})`,
				);
			});
		}
		for (const enc of encoders) this.#effect.cleanup(() => enc.close());

		this.#effect.run((effect) => {
			const allowed = effect.get(this.#allowed);
			this.#micLive.set(allowed && !!effect.get(this.#mic) && !effect.get(this.#micMuted));
			this.#cameraLive.set(allowed && !!effect.get(this.#camera) && !effect.get(this.#cameraMuted));
			this.#screenLive.set(allowed && !!effect.get(this.#screen));
			this.#preview.set({
				audio: effect.get(this.#micLive),
				video: effect.get(this.#cameraLive),
				screen: effect.get(this.#screenLive),
			});
		});
		serve(this.camera, userFields({id: identity}), this.#preview, this.#effect);
		this.#serveData();

		const ctx = new AudioContext();
		this.speaking = new SpeakingMeter(ctx, onSpeaking);
		this.#effect.cleanup(() => {
			this.speaking.close();
			void ctx.close();
		});
		this.#effect.run((effect) => {
			this.speaking.setTrack(effect.get(this.#micLive) ? effect.get(this.#mic) : undefined);
		});
	}

	#serveData(): void {
		this.camera.catalog.mutate((catalog) => {
			const ext = catalog as typeof catalog & {parley?: {data?: {track: string}}};
			ext.parley = {data: {track: DATA_TRACK}};
		});
		this.#effect.run((effect) => {
			const net = effect.get(this.camera.net);
			if (!net) return;
			const track = net.createTrack(DATA_TRACK, {latencyMax: DATA_WINDOW_MS, priority: 80});
			effect.cleanup(() => track.close());
			const producer = new Json.Snapshot.Producer<DataSnapshot>({track});
			effect.cleanup(() => producer.finish());
			effect.run((inner) => producer.update(inner.get(this.#data)));
		});
	}

	#signals(source: Track.Source): [Signal<MediaStreamTrack | undefined>, Signal<boolean> | undefined] {
		switch (source) {
			case Track.Source.Microphone:
				return [this.#mic, this.#micMuted];
			case Track.Source.Camera:
				return [this.#camera, this.#cameraMuted];
			case Track.Source.ScreenShare:
				return [this.#screen, undefined];
			case Track.Source.ScreenShareAudio:
				return [this.#screenAudio, undefined];
			default:
				return [this.#camera, this.#cameraMuted];
		}
	}

	/** Follows the publish right of the current relay token. */
	setAllowed(allowed: boolean): void {
		this.#allowed.set(allowed);
	}

	publish(pub: LocalTrackPublication): void {
		const track = pub.track;
		if (!track) return;
		const source =
			pub.source === Track.Source.Unknown
				? pub.kind === Track.Kind.Audio
					? Track.Source.Microphone
					: Track.Source.Camera
				: pub.source;
		const [trackSignal, mutedSignal] = this.#signals(source);
		if (source === Track.Source.ScreenShare) this.setScreenEncoding(pub.options?.screenShareEncoding);
		track.bindSink({
			trackChanged: (t) => trackSignal.set(t),
			muteChanged: (m) => mutedSignal?.set(m),
		});
	}

	/**
	 * Apply the stream settings' encoding to the screen renditions. Without a
	 * bitrate the encoder keeps sizing itself (pixels × 0.07 × frame rate),
	 * which for a shared screen is generous but never follows the user's
	 * choice; with one, that choice is the ceiling.
	 */
	setScreenEncoding(encoding: VideoEncoding | undefined): void {
		const frameRate = encoding?.maxFramerate;
		const maxBitrate = encoding?.maxBitrate;
		this.#screenHd.set({
			...SCREEN_HD_DEFAULT,
			...(frameRate ? {frameRate} : {}),
			...(maxBitrate ? {maxBitrate} : {}),
		});
		this.#screenSd.set({
			...SCREEN_SD_DEFAULT,
			// The fallback rung stays watchable on a thin link: a quarter of the
			// budget, and never more than 30 pictures a second.
			...(frameRate ? {frameRate: Math.min(frameRate, 30)} : {}),
			...(maxBitrate
				? {maxBitrate: Math.max(SCREEN_SD_MIN_BITRATE, Math.round(maxBitrate * SCREEN_SD_BITRATE_SHARE))}
				: {}),
		});
	}

	unpublish(pub: LocalTrackPublication): void {
		const [trackSignal, mutedSignal] = this.#signals(pub.source);
		if (pub.source === Track.Source.ScreenShare) {
			this.#screenHd.set(SCREEN_HD_DEFAULT);
			this.#screenSd.set(SCREEN_SD_DEFAULT);
		}
		trackSignal.set(undefined);
		mutedSignal?.set(false);
	}

	async publishData(data: Uint8Array, options: DataPublishOptions): Promise<void> {
		let payload = data;
		if (this.#crypto.enabled) {
			const sealed = await this.#crypto.seal(data, this.#dataAAD);
			if (!sealed) return;
			payload = sealed;
		}
		const now = Date.now();
		const message: DataMessage = {
			seq: ++this.#seq,
			at: now,
			topic: options.topic,
			to: options.destinationIdentities?.length ? options.destinationIdentities : undefined,
			payload: toBase64(payload),
			e2ee: payload !== data || undefined,
		};
		this.#data.update((prev) => ({
			messages: [...prev.messages.filter((m) => now - m.at < DATA_WINDOW_MS), message].slice(-64),
		}));
	}

	close(): void {
		this.#effect.close();
	}
}

/** Everything a remote participant's broadcast (camera or screen) provides. */
export interface RemoteMediaEvents {
	videoTrack(kind: Kind, track: MediaStreamTrack | undefined, dims?: {width: number; height: number}): void;
	audioTrack(kind: Kind, track: MediaStreamTrack | undefined): void;
	catalog(kind: Kind, hasVideo: boolean, hasAudio: boolean): void;
	preview(preview: {audio?: boolean; video?: boolean; screen?: boolean}): void;
	user(name: string | undefined): void;
	speaking(speaking: boolean, level: number): void;
	data(message: DataMessage): void;
}

/** Watches one remote broadcast and turns it into MediaStreamTracks. */
export class RemoteBroadcast {
	readonly kind: Kind;
	readonly #effect = new Effect();
	readonly #videoWanted = new Signal(false);
	readonly #videoSubscribed = new Signal(true);
	readonly #videoAttached = new Signal(false);
	readonly #audioWanted = new Signal(false);
	readonly #visible = new Signal<Watch.Video.Visible>('never');
	readonly #target = new Signal<Watch.Video.Target | undefined>({name: 'video/hd'});
	readonly #canvas = document.createElement('canvas');
	#gain?: GainNode;
	#volume = 1;
	/** Output volume and deafen of mixed audio (RemoteAudioTrack.setOutput). */
	#output?: GainNode;
	#outputVolume = 1;
	#outputMuted = false;
	#mixed = false;
	#meter?: SpeakingMeter;
	#seenData = 0;
	readonly #crypto: MediaCrypto;
	readonly #dataAAD: Uint8Array;
	/** Canvas-backed video track; frames appear once decoding runs. */
	readonly videoMediaTrack: MediaStreamTrack;
	/** What the video decoder currently receives; feeds statsReport(). */
	#videoRendition?: {name?: string; codec?: string; width?: number; height?: number; bitrate?: number};
	#videoCounters?: () => {frameCount: number; bytesReceived: number} | undefined;
	#videoRate = {frames: 0, at: 0, fps: 0};

	constructor(
		kind: Kind,
		identity: string,
		path: Moq.Path.Valid,
		connection: Moq.Connection.Reload,
		events: RemoteMediaEvents,
		crypto: MediaCrypto,
	) {
		this.kind = kind;
		this.#crypto = crypto;
		this.#dataAAD = frameAAD(identity, 'data', DATA_TRACK);
		const established = connection.established;
		// Media frames are decrypted before the decoders see them (e2eeMoq.ts).
		const broadcast = new OpenedBroadcast(
			{connection: established, enabled: true, name: path, reload: true},
			crypto,
			identity,
			kind,
		);
		this.#effect.cleanup(() => broadcast.close());

		const videoSource = new Watch.Video.Source({
			broadcast,
			supported: Watch.Video.Decoder.supported,
			target: this.#target,
		});
		const audioSource = new Watch.Audio.Source({broadcast, supported: Watch.Audio.Decoder.supported});
		this.#effect.cleanup(() => {
			videoSource.close();
			audioSource.close();
		});
		// parley: a fixed jitter buffer instead of 'real-time'. 'real-time'
		// sizes the buffer from the smallest round trip ever seen (a few tens
		// of ms), and @moq/watch drops every audio frame that arrives later
		// than that ("skipping slow group"): each drop is a hole in the voice,
		// heard as a click. A video keyframe (every 2 s) on the same
		// connection was enough to cause one - a recording from a real call
		// had its holes exactly 2 s apart. A scalar keeps the same live,
		// non-buffered playback path, just with a deeper cushion; measured
		// with scripts/browser-smoke/voice-audio.mjs (docs/voice.md).
		const sync = new Watch.Sync({
			latency: PLAYBACK_JITTER,
			connection: established,
			video: videoSource.out.jitter,
			audio: audioSource.out.jitter,
		});
		this.#effect.cleanup(() => sync.close());
		const video = new Watch.Video.Decoder(videoSource, sync, {enabled: this.#videoWanted});
		const audio = new Watch.Audio.Decoder(audioSource, sync, {enabled: this.#audioWanted});
		const renderer = new Watch.Video.Renderer(video, {canvas: this.#canvas, visible: this.#visible});
		this.#effect.cleanup(() => {
			renderer.close();
			video.close();
			audio.close();
		});

		// Which rendition the viewer ends up on, and how much of it arrives:
		// the one line that explains a blurry or stuttering picture, and the
		// source for getRTCStatsReport() (the app's stats and its "watching
		// but nothing decodes" diagnostics read that).
		this.#videoCounters = () => video.out.stats.peek();
		this.#effect.run((effect) => {
			const name = effect.get(videoSource.out.track);
			const config = effect.get(videoSource.out.config);
			if (!name || !config) {
				this.#videoRendition = undefined;
				return;
			}
			this.#videoRendition = {
				name,
				codec: config.codec,
				width: config.codedWidth,
				height: config.codedHeight,
				bitrate: config.bitrate,
			};
			console.info(
				`[parley/rtc] ${identity}/${kind}.hang video ${name} ${config.codedWidth}x${config.codedHeight} ` +
					`${config.codec} ${config.bitrate ? Math.round(config.bitrate / 1000) : '?'} kbit/s`,
			);
		});

		// Video: the renderer paints into our canvas; captureStream() turns it
		// into a MediaStreamTrack the app can attach to <video> elements.
		// The track exists from the start (so the app can attach it); frames
		// are decoded only while it is subscribed and shown somewhere.
		const videoStream = this.#canvas.captureStream();
		this.videoMediaTrack = videoStream.getVideoTracks()[0];
		this.#effect.cleanup(() => this.videoMediaTrack.stop());
		this.#effect.run((effect) => {
			this.#videoWanted.set(effect.get(this.#videoSubscribed) && effect.get(this.#videoAttached));
		});
		this.#effect.run((effect) => {
			this.#visible.set(effect.get(this.#videoWanted) ? 'always' : 'never');
		});
		this.#effect.run((effect) => {
			const frame = effect.get(renderer.out.frame);
			if (frame)
				events.videoTrack(kind, this.videoMediaTrack, {width: frame.displayWidth, height: frame.displayHeight});
		});

		// Audio: decoder output -> gain -> MediaStreamDestination, so playback,
		// volume above 100 %, output device and deafen stay in the app's hands.
		this.#effect.run((effect) => {
			const ctx = effect.get(audio.out.context);
			const root = effect.get(audio.out.root);
			if (!ctx || !root) return;
			void ctx.resume().catch(() => {});
			const gain = ctx.createGain();
			gain.gain.value = this.#volume;
			const destination = ctx.createMediaStreamDestination();
			root.connect(gain);
			gain.connect(destination);
			this.#gain = gain;
			// In the shared mixing context the audio goes straight to its
			// destination; the track above stays for the app, its element muted
			// (RemoteAudioTrack.webAudioMixed). Output volume and deafen sit
			// behind the speaking meter, so a deafened user still sees who talks.
			this.#mixed = isSharedPlaybackContext(ctx);
			console.info(
				`[parley/rtc] ${identity}/${kind}.hang audio ${ctx.sampleRate} Hz, ${this.#mixed ? 'mixed in the shared context' : 'own context via <audio>'}`,
			);
			let output: GainNode | undefined;
			if (this.#mixed) {
				output = ctx.createGain();
				output.gain.value = this.#outputMuted ? 0 : this.#outputVolume;
				gain.connect(output);
				output.connect(ctx.destination);
				this.#output = output;
			}
			const meter = new SpeakingMeter(ctx, (speaking, level) => events.speaking(speaking, level), gain);
			this.#meter = meter;
			const track = destination.stream.getAudioTracks()[0];
			events.audioTrack(kind, track);
			// What the playout stage had to hide, whenever it had to: the
			// line to look for when someone reports cuts or crackle.
			const port = (root as AudioWorkletNode).port;
			let reported = '';
			const onPlayout = (event: MessageEvent) => {
				const d = event.data as {
					type?: string;
					underruns?: number;
					expands?: number;
					concealedMs?: number;
					targetMs?: number;
				};
				if (d?.type !== 'parley-playout') return;
				const line = `underruns ${d.underruns}, gaps ${d.expands}, concealed ${d.concealedMs} ms`;
				if (line === reported) return;
				reported = line;
				console.info(`[parley/rtc] ${identity}/${kind}.hang playout: ${line}, buffer ${d.targetMs} ms`);
			};
			port?.addEventListener('message', onPlayout);
			effect.cleanup(() => {
				port?.removeEventListener('message', onPlayout);
				meter.close();
				// The decoder may already have torn its root down.
				try {
					root.disconnect(gain);
				} catch {}
				gain.disconnect();
				output?.disconnect();
				track.stop();
				this.#gain = undefined;
				this.#output = undefined;
				this.#mixed = false;
				this.#meter = undefined;
				events.audioTrack(kind, undefined);
			});
		});

		this.#effect.run((effect) => {
			const catalog = effect.get(broadcast.out.catalog) as
				| (Record<string, unknown> & {
						video?: {renditions?: Record<string, unknown>};
						audio?: {renditions?: Record<string, unknown>};
				  })
				| undefined;
			const hasVideo = !!catalog?.video && Object.keys(catalog.video.renditions ?? {}).length > 0;
			const hasAudio = !!catalog?.audio && Object.keys(catalog.audio.renditions ?? {}).length > 0;
			events.catalog(kind, hasVideo, hasAudio);
		});

		if (kind === KIND.camera) {
			const meta = consume(broadcast);
			this.#effect.cleanup(() => meta.close());
			this.#effect.run((effect) => events.preview(effect.get(meta.preview)));
			this.#effect.run((effect) => events.user(effect.get(meta.user.name)));
			this.#subscribeData(broadcast, events);
		}
	}

	#subscribeData(broadcast: Watch.Broadcast, events: RemoteMediaEvents): void {
		this.#effect.run((effect) => {
			const catalog = effect.get(broadcast.out.catalog) as {parley?: {data?: {track: string}}} | undefined;
			const active = effect.get(broadcast.out.active);
			const name = catalog?.parley?.data?.track;
			if (!active || !name) return;
			const track = active.track(name).subscribe({priority: 80});
			effect.cleanup(() => track.close());
			const consumer = new Json.Snapshot.Consumer<DataSnapshot>(track);
			effect.spawn(async () => {
				for (;;) {
					const value = await Promise.race([effect.cancel, consumer.next()]);
					if (!value) break;
					for (const m of value.messages ?? []) {
						if (m.seq > this.#seenData) {
							this.#seenData = m.seq;
							const message = await this.#openData(m);
							if (message) events.data(message);
						}
					}
				}
			});
		});
	}

	/** Decrypts an end-to-end encrypted data message; undefined drops it. */
	async #openData(m: DataMessage): Promise<DataMessage | undefined> {
		if (!m.e2ee) return this.#crypto.enabled ? undefined : m;
		const plain = await this.#crypto.open(fromBase64(m.payload), this.#dataAAD);
		return plain ? {...m, payload: toBase64(plain), e2ee: undefined} : undefined;
	}

	/**
	 * Subscription control for this broadcast's video.
	 *
	 * A camera follows the size of the box it is shown in: a thumbnail has no
	 * use for 720p. A screen share does not. Its content is text, and the
	 * viewer's tile is almost always smaller than the shared surface, so
	 * capping by tile size pinned every screen share to `video/sd` even on a
	 * fast link — the blur this strand was about (docs/strands/screenshare.md).
	 * The tile size is therefore ignored for screen shares; they stay on the
	 * rendition the app asked for, and only an explicit "low" moves them down.
	 *
	 * Leaving the choice to the watcher's own bandwidth estimate instead was
	 * measurably worse: the estimate flaps, and every flip re-configures the
	 * decoder and waits for the next key frame (1.9 pictures a second in the
	 * browser test).
	 */
	videoControl(): RemotePublicationControl {
		const screen = this.kind === KIND.screen;
		return {
			setSubscribed: (subscribed) => this.#videoSubscribed.set(subscribed),
			setVideoQuality: (quality) => this.#target.set({name: quality === VideoQuality.HIGH ? 'video/hd' : 'video/sd'}),
			setVideoDimensions: (dims) => {
				if (!screen) this.#target.set({pixels: dims.width * dims.height});
			},
		};
	}

	audioControl(): RemotePublicationControl {
		return {
			setSubscribed: (subscribed) => this.#audioWanted.set(subscribed),
			setVideoQuality: () => {},
			setVideoDimensions: () => {},
		};
	}

	/** Whether some element currently shows this broadcast's video. */
	setVideoAttached(attached: boolean): void {
		this.#videoAttached.set(attached);
	}

	setAudioWanted(wanted: boolean): void {
		this.#audioWanted.set(wanted);
	}

	/** Whether this participant's audio plays in the shared mixing context. */
	get mixed(): boolean {
		return this.#mixed;
	}

	setOutput(volume: number, muted: boolean): void {
		this.#outputVolume = volume;
		this.#outputMuted = muted;
		const output = this.#output;
		if (output) output.gain.setTargetAtTime(muted ? 0 : volume, output.context.currentTime, 0.015);
	}

	setVolume(volume: number): void {
		this.#volume = volume;
		// A ramp, not a jump: a jump in gain mid-waveform is a click.
		if (this.#gain) this.#gain.gain.setTargetAtTime(volume, this.#gain.context.currentTime, 0.015);
	}

	get meter(): SpeakingMeter | undefined {
		return this.#meter;
	}

	/**
	 * The received video as an RTCStatsReport, so the app's stats view and its
	 * screen-share watch diagnostics keep working without WebRTC. Only the
	 * entries those read are filled; the decoder implementation stays unset
	 * because WebCodecs does not tell us (and guessing would trip the
	 * software-decoder warning).
	 */
	statsReport(): RTCStatsReport | undefined {
		const rendition = this.#videoRendition;
		const counters = this.#videoCounters?.();
		if (!rendition || !counters) return undefined;
		const now = Date.now();
		if (this.#videoRate.at) {
			const elapsed = (now - this.#videoRate.at) / 1000;
			if (elapsed >= 0.2) {
				this.#videoRate = {
					frames: counters.frameCount,
					at: now,
					fps: (counters.frameCount - this.#videoRate.frames) / elapsed,
				};
			}
		} else {
			this.#videoRate = {frames: counters.frameCount, at: now, fps: 0};
		}
		const report = new Map<string, unknown>();
		report.set('moq-codec-video', {
			id: 'moq-codec-video',
			type: 'codec',
			timestamp: now,
			mimeType: `video/${codecFamily(rendition.codec)}`,
			clockRate: 90_000,
		});
		report.set('moq-inbound-video', {
			id: 'moq-inbound-video',
			type: 'inbound-rtp',
			timestamp: now,
			kind: 'video',
			mediaType: 'video',
			codecId: 'moq-codec-video',
			trackIdentifier: this.videoMediaTrack.id,
			frameWidth: rendition.width,
			frameHeight: rendition.height,
			framesPerSecond: Math.round(this.#videoRate.fps * 10) / 10,
			framesReceived: counters.frameCount,
			framesDecoded: counters.frameCount,
			bytesReceived: counters.bytesReceived,
			// MoQ runs over QUIC streams, so there are no RTP packets; the
			// frame count is the closest honest answer.
			packetsReceived: counters.frameCount,
			targetBitrate: rendition.bitrate,
		});
		return report as unknown as RTCStatsReport;
	}

	close(): void {
		this.#effect.close();
	}

	/**
	 * Close after fading out. Tearing the graph down closes the decoder's
	 * AudioContext at once, mid-waveform and with audio still buffered - a
	 * click for everyone who hears this participant leave. The fade takes the
	 * gain to silence first (time constant 10 ms, ~60 ms to inaudible).
	 */
	closeGracefully(): void {
		const gain = this.#gain;
		if (!gain) {
			this.close();
			return;
		}
		gain.gain.cancelScheduledValues(gain.context.currentTime);
		gain.gain.setTargetAtTime(0, gain.context.currentTime, 0.01);
		setTimeout(() => this.close(), GRACEFUL_CLOSE_MS);
	}
}

/** How long closeGracefully fades before tearing the graph down. */
const GRACEFUL_CLOSE_MS = 80;

/** WebCodecs codec string to the family name the app's stats expect. */
function codecFamily(codec: string | undefined): string {
	if (!codec) return 'unknown';
	if (codec.startsWith('avc1') || codec.startsWith('avc3')) return 'h264';
	if (codec.startsWith('hev1') || codec.startsWith('hvc1')) return 'h265';
	if (codec.startsWith('vp09')) return 'vp9';
	if (codec.startsWith('vp8')) return 'vp8';
	if (codec.startsWith('av01')) return 'av1';
	return codec;
}

export interface RoomDiscoveryEvents {
	added(identity: string, kind: Kind, path: Moq.Path.Valid): void;
	removed(identity: string, kind: Kind): void;
}

/** Runs the announce loop: which participants publish which broadcast. */
export function discover(
	connection: Moq.Connection.Reload,
	localIdentity: string,
	events: RoomDiscoveryEvents,
): Effect {
	const effect = new Effect();
	effect.run((inner) => {
		const established = inner.get(connection.established);
		if (!established) return;
		const announced = connection.announced();
		inner.cleanup(() => announced.close());
		const live = new Set<string>();
		inner.cleanup(() => {
			for (const key of live) {
				const [identity, kind] = key.split(' ');
				events.removed(identity, kind as Kind);
			}
		});
		inner.spawn(async () => {
			for (;;) {
				const update = await Promise.race([inner.cancel, announced.next()]);
				if (!update) break;
				const parsed = parse(update.path);
				if (!parsed || parsed.identity === localIdentity) continue;
				const key = `${parsed.identity} ${parsed.kind}`;
				if (update.active) {
					live.add(key);
					events.added(parsed.identity, parsed.kind, Moq.Path.join(announced.prefix, update.path));
				} else {
					live.delete(key);
					events.removed(parsed.identity, parsed.kind);
				}
			}
		});
	});
	return effect;
}

export type {Kind};
export {fromBase64, KIND};
